/* eslint-disable @typescript-eslint/no-explicit-any */
import { browserLogger } from "@@/settings";
import { exists, type DeepPathValueType } from "@towni/common";
import type { ZodSchema, ZodTypeAny } from "zod";
import { ZodArray, ZodEffects, ZodObject, ZodRecord, ZodUnion } from "zod";

/** If the schema is an object, traverse its shape */

const getSchemaFromZodObject = (
    schema: ZodObject<any>,
    path: string[],
): ZodSchema<unknown> => {
    const shape: any = schema.shape;
    return shape[path[0]];
};

/** For ZodEffects, we just need the value type schema */
const getSchemaFromZodEffects = (
    schema: ZodEffects<ZodTypeAny>,
): ZodSchema<unknown> => {
    const _schema = schema._def.schema as ZodSchema<unknown>;
    return _schema;
};

/**
 * For arrays, we just need the element type schema, regardless of index
 * We still parse the index to validate the format, but we don't need the actual element
 */
const getSchemaFromZodArray = (
    schema: ZodArray<ZodTypeAny>,
    path: string[],
): ZodSchema<unknown> => {
    const index = parseInt(path[0], 10);
    if (isNaN(index)) {
        throw new Error(`Invalid array index: ${path[0]}`);
    }
    return schema._def.type;
};

const getSchema = (
    schema: ZodSchema<unknown>,
    path: string[],
): ZodSchema<unknown> => {
    const isLastStep = path.length === 1;
    let result: ZodSchema<unknown> | undefined;
    if (schema instanceof ZodObject) {
        result = getSchemaFromZodObject(schema, path);
    } else if (schema instanceof ZodEffects) {
        return getSchema(getSchemaFromZodEffects(schema), path);
    } else if (schema instanceof ZodArray) {
        result = getSchemaFromZodArray(schema, path);
    } else if (schema instanceof ZodUnion) {
        //TODO: Handle union types, how to know which type to choose?
        const unionTypes = schema._def.options; //as ZodObject<unknown>[];
        //Går bara ett steg ner i unionen, borde gå flera steg
        //Osäkert på exakt hur det ska fungera
        const possibleTypes = unionTypes
            .map((t: unknown) => {
                // TODO! Potentially needs a fix
                // TODO! to handle more types than
                // TODO! just zod object, probably
                // TODO! needs to a recursive call
                // TODO! to getSchema
                // getSchema(schema, path)
                if (t instanceof ZodObject) {
                    return t.shape[path[0]];
                }
                return undefined;
            })
            .filter(exists);

        // TODO! We need to be able to loop through
        // TODO! all possible types in the union
        // TODO! and check if any of their paths
        // TODO! (and branches further down) match
        // TODO! the schema, so it's potentially a
        // TODO! performance issue if there are a lot
        // TODO! of unions within unions within unions
        // TODO! we probably need to make a test that
        // TODO! have two matches in first union
        // TODO! and then one match in one of the branches
        // TODO! later on.. 🤔
        result = possibleTypes[0];
    } else if (schema instanceof ZodRecord) {
        // For ZodRecord, we just need the value type schema, regardless of the key
        // The key itself can be any string that matches the key schema
        result = schema._def.valueType;
    }

    if (!result) {
        browserLogger.error(
            `Schema path not found: ${path.join(".")}`,
            result,
            schema,
            schema instanceof ZodObject ? schema.shape : undefined,
        );
        throw new Error(
            `Path "${path.join(".")}" could not be found in schema`,
        );
    }
    if (isLastStep) return result;
    return getSchema(result, path.slice(1));
};

/**
 * Traverse a Zod schema to find the schema at the given path.
 * @param schema - The root Zod schema to start from.
 * @param path - The path to traverse, e.g., "text2.2.textC".
 * @returns The Zod schema at the specified path or undefined if not found.
 */
function getSchemaAtPath<
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
>(schema: FormSchema, path: FieldId): ZodSchema<unknown> | undefined {
    const keys = path.split("."); // Split the path into parts

    const currentSchema: FormSchema | undefined = schema;
    if (!currentSchema) throw new Error("Schema is undefined");
    return getSchema(currentSchema, keys) as unknown as
        | ZodSchema<DeepPathValueType<FormData, FieldId>>
        | undefined;
}

export { getSchemaAtPath };
